<template>
  <div class="v-stock-chart__container" :style="{ height: heightPx }">
    <div class="v-stock-chart" :class="[theme]" v-resize:debounce.30="onResize">
      <highcharts
        class="highstock-container"
        constructor-type="stockChart"
        :options="chartOptions"
        ref="chart"
        :updateArgs="updateArgs"
      />
    </div>
  </div>
</template>

<script>
import Highcharts from 'highcharts';
import HighChartsAccessibility from 'highcharts/modules/accessibility';
import resize from 'vue-resize-directive';
import { getTimezoneOffset } from '@/utils/date';

// highcharts global options
HighChartsAccessibility(Highcharts);

Highcharts.setOptions({
  global: {
    useUTC: true
  },
  credits: {
    enabled: false // removing highcharts.com credits link
  }
});

export default {
  name: 'v-stock-chart',
  directives: {
    resize
  },
  props: {
    autoResize: {
      type: Boolean,
      default: true
    },
    options: {
      type: Object,
      required: true
    },
    updateArgs: {
      type: Array,
      default: () => [true /* redraw */, false /* oneToOne */, true /* animation */]
    },
    theme: {
      type: String,
      default: 'dark'
    },
    api: {
      type: Object
    },
    height: {
      type: Number
    }
  },
  data() {
    return {
      chart: null
    };
  },
  computed: {
    heightPx() {
      return this.height ? `${this.height}px` : '100%';
    },
    chartOptions() {
      return this._.merge(
        {
          chart: {
            styledMode: true // use css instead of svg inline styling
          },
          time: {
            timezoneOffset: -getTimezoneOffset(this.$store.state.applicationSettings.general.settings.timezone) * 60
          },
          accessibility: {
            enabled: true,
            keyboardNavigation: {
              enabled: true,
              seriesNavigation: {
                mode: 'normal'
              }
            },
            series: { pointDescriptionEnabledThreshold: false }
          }
        },
        this.options
      );
    }
  },
  watch: {
    '$store.state.applicationSettings.general.settings.applicationLanguage'() {
      this.redraw();
    },
    '$store.state.applicationSettings.general.settings.numberFormat.commaSeparator'() {
      this.redraw();
    },
    'this.chartOptions.lang.accessibility.chartContainerLabel'() {
      Highcharts.setOptions(this.chartOptions);
    }
  },
  methods: {
    get(id) {
      return this.chart.get(id);
    },
    update(newValue, ...updateArgs) {
      this.chart.update(newValue, ...updateArgs);
    },
    redraw(animation) {
      if (this.chart) {
        this.chart.redraw(animation);
      }
    },
    reflow() {
      if (this.chart) {
        this.chart.reflow();
      }
    },
    setSize(width, height) {
      if (this.chart) {
        this.chart.setSize(width, height, false);
      }
    },
    getChart() {
      return this.chart ? this.chart : null;
    },
    addSeries(options) {
      if (this.chart) {
        this.chart.addSeries(options);
      }
    },
    removeAllSeries() {
      if (this.chart) {
        while (this.chart.series.length > 0) {
          this.chart.series[0].remove(false);
        }
      }
    },
    onResize() {
      if (this.autoResize) this.reflow();
    }
  },
  mounted() {
    this.chart = this.$refs.chart.chart;
    if (this.api) {
      this.api.chart = this.chart;
      this.api.get = this.get;
      this.api.update = this.update;
      this.api.redraw = this.redraw;
      this.api.reflow = this.reflow;
      this.api.setSize = this.setSize;
      this.api.addSeries = this.addSeries;
      this.api.removeAllSeries = this.removeAllSeries;
    }
  }
};
</script>

<style lang="scss">
@import '~@/styles/components/base/v-stock-chart';
</style>
